/* Sequoia Fitness | Website: https://www.sequoia.fitness | Author: Ali Hosseini | Copyright (c) 2026 sequoia.fitness. All rights reserved. */
/* Designed and developed by Pouria Shabani | https://linkedin.com/in/pouriashabani */

import { cloneElement, useEffect, useRef, useState } from 'react'
import { MmBrand } from './icons.jsx'
import { buildGuide } from './pwaGuide.js'

const SHARE = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 3v12" />
    <path d="M8 7l4-4 4 4" />
    <path d="M5 12v7a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-7" />
  </svg>
)
const MORE = (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <circle cx="12" cy="5" r="1.9" />
    <circle cx="12" cy="12" r="1.9" />
    <circle cx="12" cy="19" r="1.9" />
  </svg>
)
const INSTALL = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="12" rx="1.5" />
    <path d="M12 8v6" />
    <path d="M9.5 11.5 12 14l2.5-2.5" />
    <path d="M8 20h8" />
  </svg>
)
export const GLYPHS = { SH: SHARE, MV: MORE, IN: INSTALL }

const GRID = [
  { href: '/', icon: 'a/ico-dash-ico.webp', label: 'سکویا', active: true },
  { href: '/#pricing', icon: 'a/ico-n-pro-ico-9457c689.webp', label: 'دریافت برنامه', scroll: 'pricing' },
  { href: '/sequoia-library/', icon: 'a/ico-vid-ico-61c5b65f.webp', label: 'ویدیوها' },
  { programs: true, icon: 'a/ico-prog-ico-09793d8d.webp', label: 'برنامه‌ها' },
  { href: '/academy', icon: 'a/sequoia-academy-ico.webp', label: 'سکویا آکادمی' },
  { href: '/sequoia-tools/', icon: 'a/ico-calc-ico2-83a64147.webp', label: 'ابزارها' },
  { href: '/links', icon: 'a/contact-us-ico.webp', label: 'تماس' },
]
const PROGS = [
  ['/fitness-program/', 'فیتنس'],
  ['/cardio-program/', 'کاردیو'],
  ['/corrective-program/', 'اصلاحی'],
  ['/flexibility-program/', 'انعطاف'],
  ['/mobility-program/', 'موبیلیتی'],
  ['/warm-cool-program/', 'گرم و سرد کردن'],
]

export default function MobileMenu() {
  const [open, setOpen] = useState(false)
  const [progs, setProgs] = useState(false)
  const [pwa, setPwa] = useState(false)
  const [guide, setGuide] = useState(null)
  const ref = useRef(null)

  useEffect(() => {
    setGuide(buildGuide())
  }, [])

  useEffect(() => {
    const root = document.documentElement
    root.classList.toggle('mm-open', open)
    const burger = document.getElementById('seq-burger')
    if (burger) burger.setAttribute('aria-expanded', open ? 'true' : 'false')
    return () => root.classList.remove('mm-open')
  }, [open])

  useEffect(() => {
    const onClick = (e) => {
      const t = e.target
      if (!t.closest) return
      if (t.closest('#seq-burger')) setOpen((v) => !v)
    }
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('click', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('click', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [])

  const scrollTo = (id) => (e) => {
    const el = document.getElementById(id)
    if (!el) return
    e.preventDefault()
    setOpen(false)
    setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 380)
  }

  const go = (g) => {
    if (g.scroll && document.getElementById(g.scroll)) {
      scrollTo(g.scroll)({ preventDefault() {} })
      return
    }
    setOpen(false)
    window.location.assign(g.href)
  }

  const togglePwa = () => {
    const v = !pwa
    setPwa(v)
    if (v) setTimeout(() => ref.current && ref.current.scrollTo({ top: ref.current.scrollHeight, behavior: 'smooth' }), 180)
  }

  return (
    <>
      <div className="mm-overlay" id="mm-overlay" onClick={() => setOpen(false)}></div>
      <aside className="mm" id="mm" aria-hidden={open ? 'false' : 'true'} dir="rtl" ref={ref}>
        <div className="mm-head">
          <a className="mm-brand" href="/" onClick={() => setOpen(false)}>
            <span className="mm-text">
              <MmBrand />
            </span>
          </a>
          <button className="mm-close" type="button" id="mm-close" aria-label="بستن منو" onClick={() => setOpen(false)}>
            <img src="a/close-ico.webp" alt="" width="40" height="40" />
          </button>
        </div>
        <div className="mm-grid">
          {GRID.map((g) =>
            g.programs ? (
              <button key={g.label} className="mm-item" type="button" id="mm-progs-btn" aria-expanded={progs ? 'true' : 'false'} aria-controls="mm-progs" onClick={() => setProgs(!progs)}>
                <span className="mm-ico">
                  <img src={g.icon} alt="" width="44" height="44" />
                </span>
                <span className="mm-lbl">{g.label}</span>
              </button>
            ) : (
              <button key={g.label} className={'mm-item' + (g.active ? ' active' : '')} type="button" onClick={() => go(g)}>
                <span className="mm-ico">
                  <img src={g.icon} alt="" width="44" height="44" />
                </span>
                <span className="mm-lbl">{g.label}</span>
              </button>
            ),
          )}
        </div>
        <div className={'mm-progs' + (progs ? ' is-open' : '')} id="mm-progs">
          {PROGS.map(([href, label]) => (
            <a key={href} href={href} onClick={() => setOpen(false)}>
              {label}
            </a>
          ))}
        </div>
        <div className="mm-dl">
          <h3>پلتفرم هوشمند سکویا فیتنس</h3>
          <div className="mm-dl-row">
            <a className="mm-dl-btn" href="a/SEQUOIA-FITNESS-V1.apk" download>
              دانلود اپلیکیشن اندروید
            </a>
            <button className="mm-dl-btn mm-dl-pwa" type="button" id="mm-pwa-btn" aria-expanded={pwa ? 'true' : 'false'} aria-controls="mm-pwa-guide" onClick={togglePwa}>
              نسخه‌ی وب‌اپلیکیشن
            </button>
          </div>
          <div className={'mm-pwa-guide' + (pwa ? ' is-open' : '')} id="mm-pwa-guide">
            <div className="mm-pwa-in">
              <div className="mm-pwa-title" id="mm-pwa-title">
                {guide ? guide.title : ''}
              </div>
              <ol className="mm-pwa-steps" id="mm-pwa-steps">
                {guide &&
                  guide.steps.map((parts, i) => (
                    <li key={i}>
                      {parts.map((p, j) => (GLYPHS[p] ? cloneElement(GLYPHS[p], { key: j }) : p))}
                    </li>
                  ))}
              </ol>
              <a className="mm-pwa-open" href="https://app.sequoia.fitness" target="_blank" rel="noopener">
                باز کردن وب‌اپلیکیشن
              </a>
            </div>
          </div>
        </div>
      </aside>
    </>
  )
}
